import React, {memo, useRef} from "react";
import styles from "./styles.module.scss";

interface OTPInputProps {
  length: number; // Number of digits in the OTP
  onComplete?: (otp: string) => void; // Callback when the OTP is fully entered
  onChange?: (otp: string) => void; // Callback when the OTP changes
}

const OTPInput: React.FC<OTPInputProps> = ({length, onComplete, onChange}) => {
  // Ref to store references to each input field
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const otpValues = Array(length).fill("");

  // Handle input change event
  const handleChange = (index: number, value: string) => {
    // Ensure only a single numeric digit (0-9)

    if (!/^\d$/.test(value)) {
      inputsRef.current[index].value = "";
      const otp = inputsRef.current.map((input) => input.value).join("");
      if (onChange) onChange(otp);

      return;
    }

    inputsRef.current[index].value = value;

    // Move focus to the next input if a digit is entered
    if (index < length - 1) {
      inputsRef.current[index + 1].focus();
    }

    // Collect all input values and call `onComplete` when the OTP is fully entered
    const otp = inputsRef.current.map((input) => input.value).join("");
    if (onChange) onChange(otp);
    if (otp.length === length) {
      if (onComplete) onComplete(otp);
      inputsRef.current[index].blur(); // Remove focus from the last input
    }
  };

  // Handle Backspace key event for moving to the previous input
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !inputsRef.current[index].value && index > 0) {
      inputsRef.current[index - 1].focus();
      const otp = inputsRef.current.map((input) => input.value).join("");
      if (onChange) onChange(otp);
    }
  };

  return (
    <div className={styles.otpContainer}>
      {otpValues.map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputsRef.current[index] = el!)} // Store reference to each input
          type="text"
          maxLength={1} // Limit each input to one character
          inputMode="numeric" // Mobile devices show a numeric keyboard
          pattern="[0-9]" // Ensure only numbers are allowed
          className={styles.otpInput}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
        />
      ))}
    </div>
  );
};

export default memo(OTPInput);